// ==============================
// 파일명 : src/pages/VideosPage.jsx
// 역할 : 영상 목록을 언어/언어팩/챕터 기준으로 필터링하고
//         영상 정보 CRUD 모달을 열 수 있도록 관리하는 화면
// 수정일 : 2025-10-12
// ==============================

import { useEffect, useMemo, useRef, useState } from "react";
import LoadingBar from "../components/LoadingBar";
import LevelBar from "../components/packs/LevelBar";
import SelectChips from "../components/packs/SelectChips";
import VideoEditorModal from "../components/videos/VideoEditorModal";
import TruncateText from "../components/common/TruncateText";
import PaginationFooter from "../components/common/PaginationFooter";
import {
  getWordPacks,
  getChaptersByPack,
  getVideos,
  addVideo,
  updateVideo,
  deleteVideo,
} from "../lib/firestore";
import { STRINGS } from "../constants/strings";
import { ytThumbUrl } from "../utils/youtube";
import { ensureBaseLanguageCounts } from "../constants/languages";

const VIDEO_FILTER_STRINGS = STRINGS.videos.filters; // 수정: 영상 필터 문구 모음 참조
const VIDEO_LIST_STRINGS = STRINGS.videos.list; // 수정: 영상 리스트 문구 모음 참조
const VIDEO_TABLE_HEADERS = VIDEO_LIST_STRINGS.headers; // 수정: 테이블 헤더 텍스트 참조
const VIDEO_ACTION_STRINGS = VIDEO_LIST_STRINGS.actions; // 수정: 액션 버튼 문구 참조
const VIDEO_NO_VIDEO_TEXT = VIDEO_LIST_STRINGS.noVideo; // 수정: 영상 미연결 안내 문구
const VIDEO_ERROR_STRINGS = STRINGS.videos.errors; // 수정: 영상 관련 에러 문구 참조

const normalize = (s) => (s || "").toString().trim(); // 수정: 문자열 정규화 함수
const contains = (hay, needle) =>
  normalize(hay).toLowerCase().includes(normalize(needle).toLowerCase()); // 수정: 검색 포함 여부 확인

const withChapterName = (chapter) => ({
  ...chapter,
  chapterName: chapter.chapterName || chapter.chapterId || chapter.title || chapter.id || "",
});

const withVideoChapterName = (video) => ({
  ...video,
  chapterName: video.chapterName || video.chapterId || "",
});

function VideosPage() { // 수정: 영상 관리 페이지 컴포넌트
  const [loading, setLoading] = useState(true);
  const [packs, setPacks] = useState([]);
  const [chaptersByPack, setChaptersByPack] = useState({});
  const [videos, setVideos] = useState([]);

  const [lang, setLang] = useState("");
  const [packId, setPackId] = useState("");
  const [chapterId, setChapterId] = useState("");

  const [q, setQ] = useState("");
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorRow, setEditorRow] = useState(null);
  const [editorVideo, setEditorVideo] = useState(null);

  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      setLoading(true);
      const [packList, videoList] = await Promise.all([getWordPacks(), getVideos()]);
      setPacks(packList);
      setVideos(videoList.map(withVideoChapterName));
      const init = {};
      for (const p of packList) {
        const chapters = await getChaptersByPack(p.id);
        init[p.id] = chapters.map(withChapterName);
      }
      setChaptersByPack(init);
      setLoading(false);
    })();
  }, []);

  const languageItems = useMemo(() => {
    const by = {};
    for (const p of packs) {
      const lng = (p.language || "").trim();
      if (!lng) continue;
      by[lng] = (by[lng] || 0) + 1;
    }
    const all = ensureBaseLanguageCounts(by);
    return Object.entries(all).map(([id, count]) => ({ id, label: `${id} (${count})` }));
  }, [packs]);

  const packsOfLang = useMemo(() => {
    if (!lang) return [];
    return packs.filter((p) => p.language === lang);
  }, [packs, lang]);

  const toggleLang = (id) => {
    setLang((prev) => (prev === id ? "" : id));
    setPackId("");
    setChapterId("");
  };
  const togglePack = (id) => {
    setPackId((prev) => (prev === id ? "" : id));
    setChapterId("");
  };
  const toggleChapter = (id) => setChapterId((prev) => (prev === id ? "" : id));

  const allChapterRows = useMemo(() => {
    if (!lang) return [];
    if (!packId) {
      const arr = [];
      for (const p of packsOfLang) {
        const chs = chaptersByPack[p.id] || [];
        for (const c of chs) arr.push({ ...c, packId: p.id, packName: p.name });
      }
      return arr;
    }
    const chs = chaptersByPack[packId] || [];
    const found = packs.find((p) => p.id === packId);
    const packName = found?.name || "";
    return chs.map((c) => ({ ...c, packId, packName }));
  }, [lang, packId, packsOfLang, chaptersByPack, packs]);

  const videoByChapterId = useMemo(() => {
    const map = new Map();
    for (const v of videos) {
      const cid = v.chapterId;
      if (cid && !map.has(cid)) map.set(cid, v);
    }
    return map;
  }, [videos]);

  const filteredRows = useMemo(() => {
    let r = allChapterRows;
    if (chapterId) r = r.filter((c) => c.chapterId === chapterId);
    if (onlyUnlinked) r = r.filter((c) => !videoByChapterId.has(c.chapterId));
    if (q) {
      r = r.filter(
        (c) =>
          contains(c.packName, q) ||
          contains(c.chapter, q) ||
          contains(c.title, q) ||
          contains(c.chapterName, q)
      );
    }
    r.sort((a, b) => {
      if (a.packName !== b.packName) return a.packName.localeCompare(b.packName);
      const ao = a.order ?? 9999;
      const bo = b.order ?? 9999;
      const P = ao - bo;
      if (P) return P;
      return a.chapterId.localeCompare(b.chapterId);
    });
    return r;
  }, [allChapterRows, lang, packId, chapterId, q, onlyUnlinked, videoByChapterId]);

  const total = filteredRows.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(Math.max(1, page), lastPage);
  const pageSlice = filteredRows.slice((safePage - 1) * perPage, safePage * perPage);
  useEffect(() => setPage(1), [q, lang, packId, chapterId, perPage, onlyUnlinked]);

  const getVideoByChapter = (cid) => videoByChapterId.get(cid);

  const openEditor = (row) => {
    const v = getVideoByChapter(row.chapterId) || null;
    setEditorRow(row);
    setEditorVideo(v);
    setEditorOpen(true);
  };
  const closeEditor = () => (setEditorOpen(false), setEditorRow(null), setEditorVideo(null));

  const saveEditor = async ({ title, videoId, subtitlesArray }) => {
    if (!editorRow) return;
    const exist = editorVideo;
    const targetPackId = exist?.packId || editorRow.packId;
    if (!targetPackId) {
      alert(VIDEO_ERROR_STRINGS.packMissing);
      return;
    }
    const subtitles = Array.isArray(subtitlesArray) ? subtitlesArray : [];
    const payload = {
      title,
      videoId,
      chapterId: editorRow.chapterId,
      chapterName: editorRow.chapterName,
      subtitles,
    };
    if (exist) {
      await updateVideo(targetPackId, exist.videoId || exist.id, payload);
    } else {
      await addVideo({ ...payload, packId: targetPackId });
    }
    const list = await getVideos();
    setVideos(list.map(withVideoChapterName));
    closeEditor();
  };

  const deleteEditor = async () => {
    if (!editorVideo) return;
    if (!window.confirm(VIDEO_LIST_STRINGS.deleteConfirm(editorVideo.title))) return; // 수정: 삭제 확인 문구 사용
    await deleteVideo(editorVideo.packId, editorVideo.videoId || editorVideo.id);
    const list = await getVideos();
    setVideos(list.map(withVideoChapterName));
    closeEditor();
  };

  const languageChips = languageItems;
  const packChips = packsOfLang.map((p) => {
    const chCount = (chaptersByPack[p.id] || []).length;
    return { id: p.id, label: `${p.name} (${chCount}) ${p.type || "free"}` };
  });

  return (
    <div className="p-4">
      <LoadingBar show={loading} />

      {/* 필터 바 */}
      <div className="grid grid-cols-1 gap-3">
        <LevelBar title={VIDEO_FILTER_STRINGS.language} color="bg-orange-500">
          <SelectChips items={languageChips} value={lang} onChange={toggleLang} getKey={(x) => x.id} getLabel={(x) => x.label} />
        </LevelBar>

        <LevelBar title={VIDEO_FILTER_STRINGS.pack} color="bg-amber-300">
          {lang ? (
            <SelectChips items={packChips} value={packId} onChange={togglePack} getKey={(x) => x.id} getLabel={(x) => x.label} />
          ) : (
            <span className="text-sm text-gray-500">{VIDEO_FILTER_STRINGS.selectLanguageFirst}</span>
          )}
        </LevelBar>

        {packId && (
          <LevelBar title={VIDEO_FILTER_STRINGS.chapter} color="bg-lime-300">
            <SelectChips
              items={(chaptersByPack[packId] || []).map((c) => ({ id: c.chapterId, label: c.chapter }))}
              value={chapterId}
              onChange={toggleChapter}
              getKey={(x) => x.id}
              getLabel={(x) => x.label}
            />
          </LevelBar>
        )}
      </div>

      {/* 검색 & 체크박스 */}
      <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <input
          type="text"
          placeholder={VIDEO_LIST_STRINGS.searchPlaceholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="border px-3 py-2 rounded w-full md:w-96"
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={onlyUnlinked} onChange={(e) => setOnlyUnlinked(e.target.checked)} />
          {VIDEO_LIST_STRINGS.onlyUnlinked}
        </label>
      </div>

      {/* 테이블 */}
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full border">
          <thead className="bg-gray-200">
            <tr>
              <th className="border px-2 py-1 text-left text-sm">{VIDEO_TABLE_HEADERS.pack}</th>
              <th className="border px-2 py-1 text-left text-sm">{VIDEO_TABLE_HEADERS.chapter}</th>
              <th className="border px-2 py-1 text-left text-sm">{VIDEO_TABLE_HEADERS.chapterName}</th>
              <th className="border px-2 py-1 text-left text-sm">{VIDEO_TABLE_HEADERS.video}</th>
              <th className="border px-2 py-1 text-center text-sm">{VIDEO_TABLE_HEADERS.actions}</th>
            </tr>
          </thead>
          <tbody>
            {pageSlice.map((row) => {
              const v = getVideoByChapter(row.chapterId);
              return (
                <tr key={row.chapterId} className="hover:bg-gray-50">
                  <td className="border px-2 py-1 text-sm">{row.packName}</td>
                  <td className="border px-2 py-1 text-sm">{row.chapter}</td>
                  <td className="border px-2 py-1 text-sm">
                    <TruncateText text={row.title || ""} maxLength={60} />
                  </td>
                  <td className="border px-2 py-1 text-sm">
                    {v ? (
                      <div className="flex items-center gap-2">
                        <img src={ytThumbUrl(v.videoId)} alt="" className="w-24 h-auto rounded" />
                        <TruncateText text={v.title || ""} maxLength={40} />
                      </div>
                    ) : (
                      <span className="text-gray-400">{VIDEO_NO_VIDEO_TEXT}</span>
                    )}
                  </td>
                  <td className="border px-2 py-1 text-center">
                    <button
                      onClick={() => openEditor(row)}
                      className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                    >
                      {v ? VIDEO_ACTION_STRINGS.edit : VIDEO_ACTION_STRINGS.add}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <PaginationFooter
        page={safePage}
        lastPage={lastPage}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
      />

      {/* 편집 모달 */}
      {editorOpen && (
        <VideoEditorModal
          open={editorOpen}
          onClose={closeEditor}
          onSave={saveEditor}
          onDelete={deleteEditor}
          video={editorVideo}
          chapter={editorRow}
        />
      )}
    </div>
  );
}

export default VideosPage;
